'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import {
  ArrowRight,
  CheckCircle2,
  XCircle,
  FileText,
  Layers,
  Clock,
  Zap,
  Pencil,
  Info,
  X,
  Save,
  Loader2,
  Minus,
  Plus,
  ChevronDown,
  ChevronUp,
  Printer
} from 'lucide-react';
import { Link, useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  registrationQueries,
  buildRegistrationFormSchema,
  useUpdateRegistrationMutation,
  type Registration,
  type RegistrationActivity
} from '@/features/registration';
import {
  stickingQueries,
  useUpdatePrintBatchMutation
} from '@/features/sticking';
import {
  dynamicFormQueries,
  DynamicFormFieldInput
} from '@/features/dynamic-forms';
import type { DynamicFormField } from '@/features/dynamic-forms';
import {
  containerTypeKeys,
  containerTypesApi
} from '@/features/container-types';
import { reviewQueries } from '../services/review.queries';
import { REVIEW_HASH } from '../constants';
import { useApproveStageMutation } from '../services/review.mutations';
import { ActivityItem } from '@/components/activity-item';
import { ReviewRejectDialog } from './review-reject-dialog';
import { ReviewApproveDialog } from './review-approve-dialog';
import { ReviewPrintView } from './review-print-view';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ReviewDetailPageProps {
  id: number;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function ReviewDetailPage({ id }: ReviewDetailPageProps) {
  const t = useTranslations('review');
  const tv = useTranslations('validation');
  const router = useRouter();
  const [comments, setComments] = useState('');
  // Overlay open-state lives in the URL hash (#approve, #reject, #edit) so
  // overlays are deep-linkable, survive refresh, and close on browser Back.
  // Destructure the stable callbacks so they can go in hook dep arrays without
  // tripping the React Compiler's manual-memoization check.
  const { open: showApproveDialog, onOpenChange: toggleApproveDialog } =
    useHashDialog(REVIEW_HASH.approve);
  const { open: showRejectDialog, onOpenChange: toggleRejectDialog } =
    useHashDialog(REVIEW_HASH.reject);
  const { open: isEditMode, onOpenChange: toggleEditMode } = useHashDialog(
    REVIEW_HASH.edit
  );
  const [showAllPages, setShowAllPages] = useState(false);
  // Transient fire-and-reset browser print (window.print) — intentionally NOT
  // hash-wired; it dwells only for the OS print dialog, not on-screen.
  const [printMode, setPrintMode] = useState(false);

  const handlePrint = useCallback(() => {
    setPrintMode(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setPrintMode(false);
      });
    });
  }, []);

  // ─── Data Fetching ──────────────────────────────────────────────────────

  const { data: regData, isLoading: regLoading } = useQuery(
    registrationQueries.detail(id)
  );
  const { data: activitiesData, isLoading: activitiesLoading } = useQuery(
    reviewQueries.activities(id)
  );
  const { data: batchMetrics } = useQuery(
    stickingQueries.printBatchMetrics(id)
  );
  const { data: printBatchesData } = useQuery(stickingQueries.printBatches(id));

  // Edit mode queries (prefetched so data is ready when entering edit mode)
  const { data: formData } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  const containerTypesSelect = useInfiniteSelect({
    queryKey: containerTypeKeys.lists(),
    queryFn: (params) => containerTypesApi.getAll(params),
    mapOption: useCallback(
      (ct: { id: number; name: string }) => ({
        value: String(ct.id),
        label: ct.name
      }),
      []
    ),
    params: { is_active: true }
  });

  const registration = regData?.data as Registration | undefined;
  const isApproved = registration?.review_completed_at != null;
  const activities = (activitiesData?.data ?? []) as RegistrationActivity[];
  const metrics = batchMetrics?.data;
  const printBatches = useMemo(
    () =>
      (printBatchesData?.data ?? []) as Array<{
        id: number;
        start_page: number;
        end_page: number;
        page_count: number;
      }>,
    [printBatchesData]
  );

  // Dynamic form fields — from fetched active form (edit mode) or from registration data
  const dynamicForm = formData?.data ?? formData;
  const fields = useMemo<DynamicFormField[]>(() => {
    if (isEditMode && dynamicForm) {
      return (dynamicForm as any)?.fields ?? [];
    }
    return (registration?.dynamic_form as any)?.fields ?? [];
  }, [isEditMode, dynamicForm, registration?.dynamic_form]);

  // ─── Mutations ──────────────────────────────────────────────────────────

  const approveMutation = useApproveStageMutation();
  const updateRegistrationMutation = useUpdateRegistrationMutation();
  const updatePrintBatchMutation = useUpdatePrintBatchMutation(id);

  const isSaving =
    updateRegistrationMutation.isPending || updatePrintBatchMutation.isPending;

  // ─── Form Setup ─────────────────────────────────────────────────────────

  const schema = useMemo(
    () => buildRegistrationFormSchema(fields, tv),
    [fields, tv]
  );

  const defaultValues = useMemo(() => {
    const formResponses: Record<string, unknown> = {};
    for (const field of fields) {
      const key = String(field.id);
      const saved = registration?.form_responses?.[key];
      if (saved != null) {
        formResponses[key] = saved;
      } else if (field.default_value) {
        formResponses[key] = field.default_value;
      } else {
        formResponses[key] = field.type === 'yes_no' ? false : '';
      }
    }
    return {
      container_type_id: registration?.container_type_id
        ? (String(registration.container_type_id) as unknown as number)
        : ('' as unknown as number),
      client_id: registration?.client_id ?? null,
      form_responses: formResponses
    };
  }, [fields, registration]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur'
  });

  // ─── Batch Edit State ─────────────────────────────────────────────────

  const [editStartPage, setEditStartPage] = useState(1);
  const [editEndPage, setEditEndPage] = useState(1);
  const [editExpectedPageCount, setEditExpectedPageCount] = useState<
    number | null
  >(null);

  // Batch derived values — use latest_batch_id OR fall back to printBatches
  const latestBatch =
    printBatches.length > 0 ? printBatches[printBatches.length - 1] : undefined;
  const batchStartPage =
    registration?.latest_batch_start_page ?? latestBatch?.start_page ?? 0;
  const batchEndPage =
    registration?.latest_batch_end_page ?? latestBatch?.end_page ?? 0;
  const latestBatchId =
    registration?.latest_batch_id ?? latestBatch?.id ?? null;
  const hasBatch = !!latestBatchId;
  const originalTotalSheets = hasBatch ? batchEndPage - batchStartPage + 1 : 0;
  const editTotalSheets = isEditMode ? editEndPage - editStartPage + 1 : 0;
  const sheetDiff = isEditMode ? editTotalSheets - originalTotalSheets : 0;

  // ─── Derived Data ──────────────────────────────────────────────────────

  const totalPrintedPages = metrics?.total_pages_printed ?? 0;

  // ─── Edit Mode Handlers ────────────────────────────────────────────────

  function enterEditMode() {
    // Edit mode open-state lives in the URL hash (#edit); the batch edit
    // state is initialized by the effect below when the hash flips on.
    toggleEditMode(true);
  }

  function cancelEditMode() {
    form.reset(defaultValues);
    setEditStartPage(batchStartPage || 1);
    setEditEndPage(batchEndPage || 1);
    setEditExpectedPageCount(registration?.expected_page_count ?? null);
    toggleEditMode(false);
  }

  // Initialize batch edit state when edit mode opens (via the Edit button or
  // a deep-linked/refreshed `#edit` hash).
  useEffect(() => {
    if (!isEditMode || !registration) return;
    setEditStartPage(
      registration.latest_batch_start_page ?? latestBatch?.start_page ?? 1
    );
    setEditEndPage(
      registration.latest_batch_end_page ?? latestBatch?.end_page ?? 1
    );
    setEditExpectedPageCount(registration.expected_page_count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, registration]);

  // Reset form when fields/registration change in edit mode
  useEffect(() => {
    if (!isEditMode || fields.length === 0 || !registration) return;
    form.reset(defaultValues);
  }, [isEditMode, fields, registration, form, defaultValues]);

  async function handleSaveAll(values: Record<string, unknown>) {
    if (!registration) return;

    try {
      // Step 1: Update registration data only if form data is available
      const hasFormData = dynamicForm && (values as any).container_type_id;
      const pageCountChanged =
        editExpectedPageCount !== registration.expected_page_count;

      if (hasFormData) {
        await updateRegistrationMutation.mutateAsync({
          id: registration.id,
          data: {
            dynamic_form_id: (dynamicForm as any).id,
            container_type_id: Number((values as any).container_type_id),
            form_responses: (values as any).form_responses,
            ...(pageCountChanged && {
              expected_page_count: editExpectedPageCount
            })
          }
        });
      } else if (pageCountChanged) {
        // Only expected_page_count changed — send minimal update
        await updateRegistrationMutation.mutateAsync({
          id: registration.id,
          data: {
            dynamic_form_id: (registration.dynamic_form as any)?.id,
            container_type_id: registration.container_type_id as number,
            form_responses: registration.form_responses ?? {},
            expected_page_count: editExpectedPageCount
          }
        });
      }

      // Step 2: Update print batch if page range changed
      const batchChanged =
        hasBatch &&
        (editStartPage !== batchStartPage || editEndPage !== batchEndPage);
      if (batchChanged && latestBatchId) {
        await updatePrintBatchMutation.mutateAsync({
          batchId: latestBatchId,
          data: { start_page: editStartPage, end_page: editEndPage }
        });
      }

      toast.success(t('saveChangesSuccess'));
      toggleEditMode(false);
    } catch {
      // Handled by global mutation error handler
    }
  }

  // ─── Other Handlers ────────────────────────────────────────────────────

  const handleApprove = async () => {
    await approveMutation.mutateAsync(id);
    toggleApproveDialog(false);
    router.push('/dashboard/review');
  };

  // ─── Loading / Not Found States ────────────────────────────────────────

  if (regLoading) {
    return (
      <div className='space-y-6'>
        <Skeleton className='h-8 w-64' />
        <div className='grid gap-6 lg:grid-cols-3'>
          <Skeleton className='h-96' />
          <Skeleton className='col-span-2 h-96' />
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='text-muted-foreground'>{t('containerNotFound')}</p>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────

  function triggerFormSubmit() {
    form.handleSubmit(handleSaveAll)();
  }

  const pageContent = (
    <div className='space-y-6'>
      {/* Edit Mode Banner */}
      {isEditMode && (
        <Alert className='border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200'>
          <Info className='h-4 w-4' />
          <AlertDescription>{t('editModeBanner')}</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className='flex flex-col gap-1'>
        <div className='flex items-center justify-between'>
          <Link
            href='/dashboard/review'
            className='text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm'
          >
            <ArrowRight className='h-4 w-4' />
            {t('backToQueue')}
          </Link>
          {/* Top Action Bar */}
          {isEditMode ? (
            <div className='flex items-center gap-2'>
              <Button
                type='button'
                onClick={triggerFormSubmit}
                className='bg-green-600 text-white hover:bg-green-700'
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                ) : (
                  <Save className='me-2 h-4 w-4' />
                )}
                {t('saveChanges')}
              </Button>
              <Button
                variant='outline'
                onClick={cancelEditMode}
                disabled={isSaving}
              >
                <X className='me-2 h-4 w-4' />
                {t('cancelEdit')}
              </Button>
            </div>
          ) : (
            <div className='flex items-center gap-2'>
              <Button variant='outline' size='sm' onClick={handlePrint}>
                <Printer className='me-2 h-4 w-4' />
                {t('printForm')}
              </Button>
              {/* {!isApproved && (
                <Button variant='outline' size='sm' onClick={enterEditMode}>
                  <Pencil className='me-2 h-4 w-4' />
                  {t('editMode')}
                </Button>
              )} */}
            </div>
          )}
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <h1 className='text-2xl font-bold tracking-tight'>
            {t('reviewTitle', { number: registration.container_number })}
          </h1>
          {(() => {
            const subStatus = registration.stage_status ?? null;
            const stageStatusStyles: Record<string, string> = {
              ready_for_review:
                'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
              returned_for_review:
                'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
              reviewed:
                'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
            };
            const stageStatusLabels: Record<string, string> = {
              ready_for_review: t('subStatusReadyForReview'),
              returned_for_review: t('subStatusReturnedForReview'),
              reviewed: t('subStatusReviewed')
            };
            if (!subStatus || !stageStatusLabels[subStatus]) return null;
            return (
              <Badge
                variant='secondary'
                className={cn('text-xs', stageStatusStyles[subStatus] ?? '')}
              >
                {stageStatusLabels[subStatus]}
              </Badge>
            );
          })()}
        </div>
        <p className='text-muted-foreground text-sm'>
          {t('submittedBy')}: {registration.creator?.name} &middot;{' '}
          {new Date(registration.created_at).toLocaleString('ar-SA', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>

      {/* Main Content */}
      <div className='grid gap-6 lg:grid-cols-3'>
        {/* RIGHT COLUMN — Container Details (2/3) */}
        <div className='order-2 space-y-6 lg:order-1 lg:col-span-2'>
          {/* Registration Info */}
          <Card
            className={cn(
              isEditMode
                ? 'border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/30'
                : 'bg-muted/30'
            )}
          >
            <CardHeader className='flex flex-row items-center justify-between pb-4'>
              <div className='flex items-center gap-2'>
                <FileText className='text-primary h-5 w-5' />
                <CardTitle className='text-base'>
                  {t('registrationInfo')}
                </CardTitle>
              </div>
              {isEditMode && (
                <Badge
                  variant='outline'
                  className='border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300'
                >
                  {t('underEditing')}
                </Badge>
              )}
            </CardHeader>
            <CardContent className='space-y-4'>
              {isEditMode ? (
                <>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <FormCombobox
                      control={form.control}
                      name='container_type_id'
                      label={t('containerType')}
                      placeholder={t('containerTypePlaceholder')}
                      infiniteConfig={containerTypesSelect}
                      required
                    />
                  </div>
                  {fields.length > 0 && (
                    <div className='grid gap-4 sm:grid-cols-2'>
                      {fields.map((field) => (
                        <DynamicFormFieldInput
                          key={field.id}
                          field={field}
                          control={form.control}
                          name={`form_responses.${String(field.id)}`}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                    <div className='space-y-1'>
                      <p className='text-muted-foreground text-xs'>
                        {t('containerType')}
                      </p>
                      <p className='bg-muted/50 rounded-md border px-3 py-2 text-sm font-medium'>
                        {registration.container_type?.name ?? '—'}
                      </p>
                    </div>
                  </div>
                  {fields.length > 0 && (
                    <>
                      <div className='border-border border-t' />
                      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {fields.map((field) => {
                          const value = registration.form_responses?.[field.id];
                          const displayValue =
                            value != null && value !== ''
                              ? String(value)
                              : field.default_value
                                ? String(field.default_value)
                                : '—';
                          return (
                            <div key={field.id} className='space-y-1'>
                              <p className='text-muted-foreground text-xs'>
                                {field.label}
                              </p>
                              <p className='bg-muted/50 rounded-md border px-3 py-2 text-sm font-medium'>
                                {displayValue}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Bottom Save All Button (Edit Mode) */}
          {isEditMode && (
            <div className='space-y-3'>
              <Button
                type='button'
                onClick={triggerFormSubmit}
                className='w-full bg-green-600 text-white hover:bg-green-700'
                size='lg'
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className='me-2 h-5 w-5 animate-spin' />
                ) : (
                  <Save className='me-2 h-5 w-5' />
                )}
                {t('saveAllChanges')}
              </Button>
            </div>
          )}
        </div>

        {/* LEFT COLUMN — Review Actions + Activity (1/3) */}
        <div className='order-1 space-y-6 lg:order-2'>
          {/* Review Summary Card */}
          <Card
            className={cn(isEditMode && 'border-blue-200 dark:border-blue-800')}
          >
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <Zap className='text-primary h-5 w-5' />
              <CardTitle className='text-base'>{t('reviewSummary')}</CardTitle>
              {isEditMode ? (
                <Badge
                  variant='outline'
                  className='ms-auto border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300'
                >
                  {t('underEditing')}
                </Badge>
              ) : null}
            </CardHeader>
            <CardContent className='space-y-4'>
              {!isApproved && (
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    {t('auditComments')}
                  </label>
                  <Textarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder={t('auditCommentsPlaceholder')}
                    rows={4}
                  />
                </div>
              )}

              {isApproved ? (
                <div className='flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 dark:border-green-800 dark:bg-green-950'>
                  <CheckCircle2 className='h-5 w-5 text-green-600 dark:text-green-400' />
                  <span className='text-sm font-medium text-green-700 dark:text-green-300'>
                    {t('approveSuccess')}
                  </span>
                </div>
              ) : isEditMode ? (
                <Button
                  type='button'
                  onClick={triggerFormSubmit}
                  className='w-full bg-green-600 text-white hover:bg-green-700'
                  size='lg'
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className='me-2 h-5 w-5 animate-spin' />
                  ) : (
                    <Save className='me-2 h-5 w-5' />
                  )}
                  {t('saveChanges')}
                </Button>
              ) : (
                <>
                  <Button
                    className='w-full bg-green-600 text-white hover:bg-green-700'
                    size='lg'
                    onClick={() => toggleApproveDialog(true)}
                  >
                    <CheckCircle2 className='me-2 h-5 w-5' />
                    {t('approveContainer')}
                  </Button>

                  <Button
                    variant='outline'
                    size='lg'
                    className='w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700'
                    onClick={() => toggleRejectDialog(true)}
                  >
                    <XCircle className='me-2 h-5 w-5' />
                    {t('rejectAndCorrect')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Review Log */}
          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <Clock className='text-primary h-5 w-5' />
              <CardTitle className='text-base'>{t('reviewLog')}</CardTitle>
            </CardHeader>
            <CardContent>
              {activitiesLoading ? (
                <div className='space-y-3'>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className='h-16 w-full' />
                  ))}
                </div>
              ) : activities.length === 0 ? (
                <p className='text-muted-foreground text-sm'>
                  {t('noActivities')}
                </p>
              ) : (
                <div className='relative space-y-0'>
                  {activities.map((activity, index) => (
                    <ActivityItem
                      key={activity.id}
                      activity={activity}
                      isLast={index === activities.length - 1}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approve Dialog — #approve */}
      <ReviewApproveDialog
        open={showApproveDialog}
        onOpenChange={toggleApproveDialog}
        hasBatch={hasBatch}
        originalTotalSheets={originalTotalSheets}
        isApproving={approveMutation.isPending}
        onApprove={handleApprove}
      />

      {/* Reject Dialog — #reject */}
      <ReviewRejectDialog
        open={showRejectDialog}
        onOpenChange={toggleRejectDialog}
        registrationId={id}
        onSuccess={() => router.push('/dashboard/review')}
      />

      {printMode && (
        <ReviewPrintView
          registration={registration}
          fields={fields}
          activities={activities}
        />
      )}
    </div>
  );

  // Wrap in FormProvider when in edit mode so form controls can access context
  if (isEditMode) {
    return (
      <Form form={form} onSubmit={form.handleSubmit(handleSaveAll)}>
        {pageContent}
      </Form>
    );
  }

  return pageContent;
}
